import type { Answer } from '../api/generated/Answer'
import type { Question } from '../api/generated/Question'

export type AnswerCount = { value: string; count: number }

export type QuestionSummary = { respondents: number; counts: AnswerCount[] }

/**
 * How many volunteers gave each answer to a question: a choice's options first, in their order
 * and even when unused, then the other answers (removed options, free text) by frequency.
 */
export function summarize(question: Question, answers: readonly Answer[]): QuestionSummary {
  const counts = new Map<string, number>(question.options.map((option) => [option, 0]))
  let respondents = 0
  for (const answer of answers) {
    if (answer.question_id !== question.id) continue
    respondents += 1
    counts.set(answer.value, (counts.get(answer.value) ?? 0) + 1)
  }
  const options = new Set(question.options)
  const others = [...counts]
    .filter(([value]) => !options.has(value))
    .sort(([a, countA], [b, countB]) => countB - countA || a.localeCompare(b, 'fr'))
  return {
    respondents,
    counts: [
      ...question.options.map((option) => [option, counts.get(option) ?? 0] as const),
      ...others,
    ].map(([value, count]) => ({ value, count })),
  }
}
